// Cross-Module Health - health check and performance metrics logic
// extracted from CrossModuleIntegration
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import debugLogger from "../utils/debugLogger";

import {
  IntegrationConfig,
  IntegrationMetrics,
  ModuleHealthStatus,
  ModuleState,
  PerformanceMetrics,
} from "./types";

/**
 * Shared mutable state passed in from the CrossModuleIntegration facade.
 * The health module mutates these maps directly to preserve behavior.
 */
export interface HealthState {
  config: IntegrationConfig;
  metrics: IntegrationMetrics;
  moduleStates: Map<string, ModuleState>;
  performanceMetrics: Map<string, PerformanceMetrics>;
  healthChecks: Map<string, ModuleHealthStatus>;
}

/**
 * Health checker: tracks module health status, performance metrics, and
 * runs periodic health/performance checks. Mutates the shared state passed in.
 */
export class CrossModuleHealth {
  constructor(private state: HealthState) {}

  /**
   * Get module health status
   */
  public getModuleHealthStatus(moduleName: string): ModuleHealthStatus {
    return (
      this.state.healthChecks.get(moduleName) || ModuleHealthStatus.UNKNOWN
    );
  }

  /**
   * Get performance metrics for a module
   */
  public getModulePerformanceMetrics(
    moduleName: string,
  ): PerformanceMetrics | null {
    return this.state.performanceMetrics.get(moduleName) || null;
  }

  /**
   * Update performance metrics for a module after an interaction.
   */
  public updatePerformanceMetrics(
    moduleName: string,
    latency: number,
    success: boolean,
  ): void {
    const { state } = this;
    const metrics = state.performanceMetrics.get(moduleName) || {
      averageLatency: 0,
      totalEvents: 0,
      errorRate: 0,
      lastUpdated: new Date(),
    };

    metrics.totalEvents++;
    metrics.averageLatency =
      (metrics.averageLatency * (metrics.totalEvents - 1) + latency) /
      metrics.totalEvents;

    if (!success) {
      metrics.errorRate =
        (metrics.errorRate * (metrics.totalEvents - 1) + 1) /
        metrics.totalEvents;
    }

    metrics.lastUpdated = new Date();
    state.performanceMetrics.set(moduleName, metrics);

    // Check performance threshold
    if (latency > state.config.performanceThreshold) {
      state.metrics.performanceViolations++;
      debugLogger.warn(
        "integration",
        `Performance threshold exceeded for module: ${moduleName}`,
        { latency, threshold: state.config.performanceThreshold },
      );
    }
  }

  /**
   * Set up periodic performance monitoring.
   */
  public setupPerformanceMonitoring(): void {
    const { state } = this;
    if (!state.config.enablePerformanceMonitoring) {
      return;
    }

    // Set up periodic performance monitoring
    setInterval(() => {
      this.monitorPerformance();
    }, 5000);
  }

  /**
   * Set up periodic health checks.
   */
  public setupHealthChecks(): void {
    const { state } = this;
    if (!state.config.enableHealthChecks) {
      return;
    }

    // Set up periodic health checks
    setInterval(() => {
      this.performHealthChecks();
    }, state.config.healthCheckInterval);
  }

  /**
   * Remove health/performance tracking for an unregistered module.
   */
  public handleModuleUnregistered(moduleName: string): void {
    const { state } = this;
    state.healthChecks.delete(moduleName);
    state.performanceMetrics.delete(moduleName);
  }

  private monitorPerformance(): void {
    const { state } = this;
    state.performanceMetrics.forEach((metrics, moduleName) => {
      if (metrics.errorRate > 0.1) {
        // 10% error rate threshold
        debugLogger.warn(
          "integration",
          `High error rate detected for module: ${moduleName}`,
          { errorRate: metrics.errorRate },
        );
      }

      if (metrics.averageLatency > state.config.performanceThreshold) {
        debugLogger.warn(
          "integration",
          `High latency detected for module: ${moduleName}`,
          { averageLatency: metrics.averageLatency },
        );
      }
    });
  }

  private performHealthChecks(): void {
    const { state } = this;
    state.moduleStates.forEach((moduleState, moduleName) => {
      const lastActivity = moduleState.lastActivity.getTime();
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;

      if (timeSinceLastActivity > 300000) {
        // 5 minutes
        state.healthChecks.set(moduleName, ModuleHealthStatus.UNHEALTHY);
        state.metrics.healthCheckFailures++;
        debugLogger.warn(
          "integration",
          `Module health check failed: ${moduleName}`,
          { timeSinceLastActivity },
        );
      } else {
        state.healthChecks.set(moduleName, ModuleHealthStatus.HEALTHY);
      }
    });
  }
}
